/**
 * The editor's writes, as row patches (PLAN §6.1): create, set fields, edit a
 * script, move, soft delete. Pure over the session's `Writer`: each builder
 * stamps a fresh HLC and clientOpId, and a block of moves shares one HLC
 * (PLAN §6.2 "A drags a block of 5 parts: one multi-row patch batch that
 * shares a single HLC").
 */
import type { RowKind } from '@acp/domain/fields.js';
import { ordBetween } from '@acp/domain/ordering.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import type { Row, RowPatch } from '@acp/domain/row.js';
import { textReplaceUpdate } from '@acp/domain/text.js';
import { childrenOf, type RowTree } from '@acp/domain/tree.js';
import type { Writer } from '@acp/api-client/writer.js';

export type Move = { readonly rowId: string; readonly parentId: string; readonly ord: string };

/** The piece type a new piece or adlib starts as: the show style's first (`pieceTypes.ts`), or none. */
export type NewPieceType = { readonly id: string; readonly label: string; readonly sourceLayer: string };

/**
 * What a new row of each kind starts with, so it is recognisable in the tree before anyone names it. A piece or
 * an adlib takes `pieceType` (its label, id and source layer) when the editor knows the show style's types.
 */
export function initialFields(kind: 'segment' | 'part' | 'piece' | 'adlib', pieceType: NewPieceType | null): Record<string, JsonValue> {
  switch (kind) {
    case 'segment':
      return { name: 'New segment' };
    case 'part':
      return { title: 'New part' };
    case 'piece':
    case 'adlib': {
      const fallback = kind === 'piece' ? 'New piece' : 'New adlib';
      return pieceType === null ? { name: fallback } : { name: pieceType.label, pieceType: pieceType.id, sourceLayer: pieceType.sourceLayer };
    }
  }
}

/**
 * The `ord` for a new child of `parentId` right after `afterId` (null: at the
 * end), strictly between its visible neighbours.
 */
export function ordForInsert(tree: RowTree, parentId: string, afterId: string | null, writerTag: string): string {
  const siblings = childrenOf(tree, parentId);
  if (afterId === null) return ordBetween(siblings.at(-1)?.ord ?? null, null, writerTag);
  const index = siblings.findIndex((r) => r.rowId === afterId);
  if (index < 0) return ordBetween(siblings.at(-1)?.ord ?? null, null, writerTag);
  return ordBetween(siblings[index]?.ord ?? null, siblings[index + 1]?.ord ?? null, writerTag);
}

export function createPatch(writer: Writer, rundownId: string, kind: RowKind, parentId: string, ord: string, set: Readonly<Record<string, JsonValue>>): RowPatch {
  return {
    rowId: writer.newId(),
    set: { ...set, parentId, ord },
    create: { kind, rundownId, source: { system: 'native' }, lockedFields: [] },
    hlc: writer.clock.now(),
    origin: writer.nodeId,
    clientOpId: writer.newOpId(),
  };
}

export function setPatch(writer: Writer, rowId: string, set: Readonly<Record<string, JsonValue>>): RowPatch {
  return { rowId, set: { ...set }, hlc: writer.clock.now(), origin: writer.nodeId, clientOpId: writer.newOpId() };
}

/** Soft delete (PLAN §6.1): `deleted: true`, one register; the rows sharing one HLC. */
export function deletePatches(writer: Writer, rowIds: readonly string[]): RowPatch[] {
  const hlc = writer.clock.now();
  return rowIds.map((rowId) => ({ rowId, set: { deleted: true }, hlc, origin: writer.nodeId, clientOpId: writer.newOpId() }));
}

/** A block move: every row's `parentId` + `ord` under one HLC. */
export function movePatches(writer: Writer, moves: readonly Move[]): RowPatch[] {
  const hlc = writer.clock.now();
  return moves.map((m) => ({ rowId: m.rowId, set: { parentId: m.parentId, ord: m.ord }, hlc, origin: writer.nodeId, clientOpId: writer.newOpId() }));
}

/**
 * A script (text field) edit: the Yjs update that turns the row's current
 * text state into `next`, replacing only the span that differs — so what
 * someone else typed concurrently elsewhere survives the merge (PLAN §6.2
 * "Two people edit the same script"). Null when nothing changed.
 */
export function textPatch(writer: Writer, row: Row, field: string, next: string): RowPatch | null {
  const current = typeof row.f[field] === 'string' ? row.f[field] : '';
  if (current === next) return null;
  const state = Object.hasOwn(row.text, field) ? (row.text[field] ?? null) : null;
  const update = textReplaceUpdate(state, next, writer.textClientId());
  return { rowId: row.rowId, set: {}, text: { [field]: update }, hlc: writer.clock.now(), origin: writer.nodeId, clientOpId: writer.newOpId() };
}

function isJsonObject(value: JsonValue): value is { [key: string]: JsonValue } {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** An object value as dotted leaves under `prefix` (lists and primitives are leaves; an empty object is one). */
function leavesOf(prefix: string, value: JsonValue, out: Record<string, JsonValue>): void {
  if (!isJsonObject(value) || Object.keys(value).length === 0) {
    out[prefix] = value;
    return;
  }
  for (const [key, child] of Object.entries(value)) leavesOf(`${prefix}.${key}`, child, out);
}

/**
 * The fields that set one param of a piece or adlib row (`params` is an open
 * namespace: one LWW register per dotted leaf, PLAN §6.1). An object value is
 * written as its leaves (`params.style.theme`, …), so two people editing two
 * fields of one object param both win, and a leaf another writer made
 * (`acp patch --set params.vs.clip=…`) is overwritten rather than left to win
 * over a whole-object write when the row is read (`expandFieldPaths` applies
 * deeper paths last). What the row holds under the param that the new value
 * does not — a whole-object register, a leaf of a key now gone — is cleared
 * (null: absent). A list or a primitive is one register; a blank optional
 * param is null.
 */
export function paramSetFields(f: Readonly<Record<string, JsonValue>>, key: string, value: JsonValue): Record<string, JsonValue> {
  const root = `params.${key}`;
  const set: Record<string, JsonValue> = {};
  leavesOf(root, value, set);
  const written = Object.keys(set);
  for (const path of Object.keys(f)) {
    if (path !== root && !path.startsWith(`${root}.`)) continue;
    if (Object.hasOwn(set, path) || f[path] === null) continue;
    // A stale path under a new non-null leaf (a list or primitive where an object was) cannot be cleared without
    // shadowing that leaf: a null there would be read after it and win. It is left; such a type change is a
    // show-style edit, not a form's. Under a cleared param (null), its leaves are cleared too.
    if (written.some((leaf) => set[leaf] !== null && path.startsWith(`${leaf}.`))) continue;
    set[path] = null;
  }
  return set;
}
